import React, { useMemo, useState } from "react";
import "./AdminFleetCalendar.css";

function startOfToday() {
  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
}

function addDays(date, amount) {
  const nextDate = new Date(date);

  nextDate.setDate(nextDate.getDate() + amount);

  return nextDate;
}

function toDateString(date) {
  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isSameDay(firstDate, secondDate) {
  return (
    firstDate.getFullYear() ===
      secondDate.getFullYear() &&
    firstDate.getMonth() ===
      secondDate.getMonth() &&
    firstDate.getDate() ===
      secondDate.getDate()
  );
}

function AdminFleetCalendar({
  cars,
  bookings,
  selectedCarId,
  onSelectCar,
}) {
  const [calendarStart, setCalendarStart] =
    useState(startOfToday());

  const today = startOfToday();

  const calendarDays = useMemo(() => {
    return Array.from(
      { length: 7 },
      (_, index) =>
        addDays(calendarStart, index)
    );
  }, [calendarStart]);

  const calendarBookings = useMemo(() => {
    return bookings.filter(
      (booking) =>
        booking.status === "confirmed" ||
        booking.status === "pending"
    );
  }, [bookings]);

  const getBookingsForCarAndDay = (
    carId,
    date
  ) => {
    const dateString = toDateString(date);

    return calendarBookings.filter(
      (booking) =>
        Number(booking.car_id) ===
          Number(carId) &&
        booking.pickup_date <= dateString &&
        booking.return_date >= dateString
    );
  };

  const goPrevious = () => {
    setCalendarStart((current) =>
      addDays(current, -7)
    );
  };

  const goNext = () => {
    setCalendarStart((current) =>
      addDays(current, 7)
    );
  };

  const goToday = () => {
    setCalendarStart(startOfToday());
  };

  const formatRange = () => {
    const firstDay =
      calendarDays[0];

    const lastDay =
      calendarDays[
        calendarDays.length - 1
      ];

    const firstLabel =
      firstDay.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
        }
      );

    const lastLabel =
      lastDay.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );

    return `${firstLabel} – ${lastLabel}`;
  };

  return (
    <section className="fleet-calendar-card">
      <div className="fleet-calendar-heading">
        <div>
          <span className="fleet-calendar-eyebrow">
            VEHICLE AVAILABILITY
          </span>

          <h2>Fleet Calendar</h2>

          <p>
            Confirmed reservations block
            availability. Pending requests are
            shown separately.
          </p>
        </div>

        <div className="fleet-calendar-navigation">
          <button
            type="button"
            onClick={goPrevious}
          >
            ← Previous
          </button>

          <button
            type="button"
            className="fleet-today-button"
            onClick={goToday}
          >
            Today
          </button>

          <button
            type="button"
            onClick={goNext}
          >
            Next →
          </button>
        </div>
      </div>

      <div className="fleet-calendar-range">
        <strong>{formatRange()}</strong>

        <div className="fleet-calendar-legend">
          <span>
            <i className="fleet-legend-confirmed" />
            Confirmed
          </span>

          <span>
            <i className="fleet-legend-pending" />
            Pending
          </span>
        </div>
      </div>

      {selectedCarId && (
        <div className="fleet-filter-banner">
          <span>
            Booking list filtered to Car #
            {selectedCarId}
          </span>

          <button
            type="button"
            onClick={() =>
              onSelectCar(null)
            }
          >
            Show All Cars
          </button>
        </div>
      )}

      <div className="fleet-calendar-scroll">
        <div className="fleet-calendar-grid">
          <div className="fleet-calendar-header-row">
            <div className="fleet-vehicle-header">
              Vehicle
            </div>

            {calendarDays.map((date) => (
              <div
                key={toDateString(date)}
                className={`fleet-day-header ${
                  isSameDay(date, today)
                    ? "today"
                    : ""
                }`}
              >
                <span>
                  {date
                    .toLocaleDateString(
                      "en-GB",
                      {
                        weekday:
                          "short",
                      }
                    )
                    .toUpperCase()}
                </span>

                <strong>
                  {date.getDate()}
                </strong>

                <small>
                  {date.toLocaleDateString(
                    "en-GB",
                    {
                      month: "short",
                    }
                  )}
                </small>
              </div>
            ))}
          </div>

          {cars.map((car) => (
            <div
              className={`fleet-calendar-row ${
                Number(selectedCarId) ===
                Number(car.id)
                  ? "selected"
                  : ""
              }`}
              key={car.id}
            >
              <button
                type="button"
                className="fleet-car-cell"
                onClick={() =>
                  onSelectCar(car.id)
                }
              >
                <div className="fleet-car-number">
                  #{car.id}
                </div>

                <div>
                  <strong>
                    {car.name}
                  </strong>

                  <span>
                    €{car.price}/day
                  </span>
                </div>
              </button>

              {calendarDays.map(
                (date) => {
                  const dayBookings =
                    getBookingsForCarAndDay(
                      car.id,
                      date
                    );

                  const confirmed =
                    dayBookings.filter(
                      (booking) =>
                        booking.status ===
                        "confirmed"
                    );

                  const pending =
                    dayBookings.filter(
                      (booking) =>
                        booking.status ===
                        "pending"
                    );

                  const occupied =
                    confirmed.length > 0 ||
                    pending.length > 0;

                  return (
                    <button
                      type="button"
                      key={`${car.id}-${toDateString(
                        date
                      )}`}
                      className={`fleet-calendar-cell ${
                        isSameDay(
                          date,
                          today
                        )
                          ? "today"
                          : ""
                      } ${
                        confirmed.length
                          ? "has-confirmed"
                          : ""
                      } ${
                        !confirmed.length &&
                        pending.length
                          ? "has-pending"
                          : ""
                      }`}
                      onClick={() => {
                        if (occupied) {
                          onSelectCar(
                            car.id
                          );
                        }
                      }}
                      title={
                        occupied
                          ? `${confirmed.length} confirmed, ${pending.length} pending`
                          : "Available"
                      }
                    >
                      {confirmed.length >
                      0 ? (
                        <div className="fleet-reservation confirmed">
                          <strong>
                            Booked
                          </strong>

                          <span>
                            {confirmed.length ===
                            1
                              ? `#${confirmed[0].id}`
                              : `${confirmed.length} bookings`}
                          </span>
                        </div>
                      ) : pending.length >
                        0 ? (
                        <div className="fleet-reservation pending">
                          <strong>
                            Pending
                          </strong>

                          <span>
                            {pending.length ===
                            1
                              ? `#${pending[0].id}`
                              : `${pending.length} requests`}
                          </span>
                        </div>
                      ) : (
                        <span className="fleet-available-dot">
                          Available
                        </span>
                      )}
                    </button>
                  );
                }
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default AdminFleetCalendar;